import React, { useEffect } from 'react';
import { useDuplicateChecker } from '../utils/duplicateChecker';

/**
 * DuplicateChecker Component
 * Provides real-time duplicate validation for form inputs
 * 
 * @param {string} module - Module to check: 'incident-classifications', 'action-cards', 'teams'
 * @param {string} value - Current input value to check
 * @param {string} excludeId - ID to exclude from duplicate check (for updates)
 * @param {function} onValidationChange - Callback when validation status changes
 * @param {boolean} disabled - Whether duplicate checking is disabled
 * @param {string} className - Additional CSS classes
 */
const DuplicateChecker = ({ 
  module, 
  value, 
  excludeId = null, 
  onValidationChange = () => {}, 
  disabled = false,
  className = "" 
}) => {
  const { isDuplicate, isChecking, message, checkName, clearStatus } = useDuplicateChecker(module, excludeId);

  // Check for duplicates when value changes
  useEffect(() => {
    if (disabled) {
      clearStatus();
      return;
    }

    if (value && value.trim()) {
      checkName(value);
    } else {
      clearStatus();
    }
  }, [value, disabled, checkName, clearStatus]);

  // Notify parent component of validation status
  useEffect(() => {
    onValidationChange({
      isChecking,
      isDuplicate,
      message,
      isValid: !isDuplicate && !isChecking
    });
  }, [isChecking, isDuplicate, message, onValidationChange]);

  // Don't render anything if checking is disabled or no value
  if (disabled || !value || !value.trim()) {
    return null;
  }

  return (
    <div className={`mt-1 ${className}`}>
      {isChecking && (
        <div className="flex items-center text-sm text-gray-600">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Checking for duplicates...
        </div>
      )}
      
      {!isChecking && isDuplicate && (
        <div className="flex items-start text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          <svg className="flex-shrink-0 mr-2 mt-0.5 h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{message}</span>
        </div>
      )}
      
      {/* Success message removed per UI update requirements */}
    </div>
  );
};

export default DuplicateChecker;